import type { Kitchen } from '../../../kitchens/kitchen.class';
import type { Panel } from '../../../panels/panel.class';
import type { Island } from '../../../islands/island.class';
import { PanelItemGroup } from '../../../panel-item-groups/panel-item-group.class';
import type { PanelItemDataIn } from '../../../panel-items/types';
import { requiresVenting } from '../../../rules-engine/rules/requires-venting/requires-venting';
import { cleanIslandCfn } from '../../../islands/functions/clean-island.cfn';
import { isRuleGeneratedCompanionCfn } from './companion-items.cfn';
import { hypotheticalGroupWidthsCfn, predictPanelGrowthCfn, resolveGrowthCfn } from './predict-panel-growth.cfn';
import { IslandResize, MoveGroupToPanelResult, RearrangeGrowthOptions, RearrangeRefusedError } from './rearrange.types';

/**
 * Serialized column, with only the members a move carries. Taken BEFORE any
 * deletion so the rules that run on delete cannot change what is carried.
 */
export interface GroupCarry {
	groupId: string;
	showCenterDimension: boolean;
	items: Array<PanelItemDataIn>;
	movedIds: Array<string>;
	allItemIds: Array<string>;
	width: number;
}

export const takeGroupCarryCfn = (group: PanelItemGroup): GroupCarry => {
	const carried = group.items.children.filter((pi) => !isRuleGeneratedCompanionCfn(pi));
	if (carried.length === 0) {
		throw new RearrangeRefusedError('nothing-to-move', `Group '${group.id}' holds only rule-generated parts.`);
	}
	if (group.positionLocked) {
		throw new RearrangeRefusedError('not-movable', `Group '${group.id}' is position-locked.`);
	}
	return {
		groupId: group.id,
		showCenterDimension: group.showCenterDimension,
		items: carried.map((pi) => pi.serialize() as PanelItemDataIn),
		movedIds: carried.map((pi) => pi.id),
		allItemIds: group.items.children.map((pi) => pi.id),
		width: group.maxOutsideWidth
	};
};

/**
 * Deletes every member through `PanelItems.delete`, so each one publishes
 * `panel_item.deleted` (the trim-kit and keg-tap rules key on it), the empty
 * column is purged and the reference cache is cleared for each id.
 */
export const removeGroupCfn = (kitchen: Kitchen, group: PanelItemGroup, carry: GroupCarry): void => {
	for (const id of carry.allItemIds) {
		group.items.delete(id);
	}
	kitchen.context.cache.delete(carry.groupId);
};

/**
 * Rebuilds the column under `targetPanel` at `x` from its serialized members,
 * ids preserved. The column and item instances are NEW objects: the old ones
 * captured their ancestors at construction and cannot be re-parented. Each
 * member is added through `PanelItems.new`, so `panel_items.add` fires and the
 * rules engine regenerates the trim kit / jacket / vents at the destination.
 */
export const placeGroupCarryCfn = (kitchen: Kitchen, targetPanel: Panel, carry: GroupCarry, x: number): PanelItemGroup => {
	const group = new PanelItemGroup({
		dataIn: {
			id: carry.groupId,
			center: { x, y: 0, z: 0 },
			showCenterDimension: carry.showCenterDimension,
			positionLocked: false,
			items: { children: [] }
		},
		parent: targetPanel.panelItemGroups
	});
	targetPanel.panelItemGroups.children.push(group);

	for (const itemData of carry.items) {
		group.items.new(itemData);
	}

	// anything cached against these ids now points at the old island
	kitchen.context.cache.delete([carry.groupId, ...carry.movedIds]);

	return group;
};

/** Vents follow fuel appliances: re-evaluate an island a column just left, since the venting rule only runs on add. */
export const reventIslandCfn = (island: Island): void => {
	requiresVenting({ refMap: island.refMap });
};

/**
 * Moves one appliance column to another panel (normally another island's front
 * panel) at an absolute x, growing the receiving island when it must.
 *
 * Order of operations is the one the companion rules need: delete first (the
 * fridge's trim kit goes with it), then add (the rule creates a fresh kit next
 * to the fridge). Refusals are decided BEFORE the first mutation.
 */
export const moveApplianceGroupToPanelCfn = (kitchen: Kitchen, group: PanelItemGroup, targetPanel: Panel, x: number, options: RearrangeGrowthOptions = {}): MoveGroupToPanelResult => {
	const sourcePanel = group.refMap.panel as Panel | null;
	const sourceIsland = group.refMap.island as Island | null;
	const targetIsland = targetPanel.refMap.island as Island | null;
	if (!sourcePanel || !sourceIsland || !targetIsland) {
		throw new Error('moveApplianceGroupToPanel: the group and the target panel must both belong to islands.');
	}

	if (sourcePanel.id === targetPanel.id) {
		targetPanel.placeGroupAt(group.id, x);
		return { groupId: group.id, movedIds: group.items.children.map((pi) => pi.id), resized: null };
	}

	const carry = takeGroupCarryCfn(group);

	const prediction = predictPanelGrowthCfn(targetIsland, targetPanel, hypotheticalGroupWidthsCfn(targetPanel, [], [carry.width]));
	const growth = resolveGrowthCfn(targetIsland, prediction, options);

	removeGroupCfn(kitchen, group, carry);
	placeGroupCarryCfn(kitchen, targetPanel, carry, x);

	// both runs re-space: hand-placed positions on either panel are forgotten
	sourcePanel.resetFineTune();
	targetPanel.resetFineTune();

	reventIslandCfn(sourceIsland);
	cleanIslandCfn(sourceIsland, { autoResize: false });

	const resizedTo = cleanIslandCfn(targetIsland, { autoResize: growth !== null, growOnly: true });
	const resized: IslandResize | null = resizedTo !== null && growth
		? { islandId: targetIsland.id, fromWidthIn: growth.fromWidthIn, toWidthIn: resizedTo }
		: null;

	return { groupId: carry.groupId, movedIds: carry.movedIds, resized };
};
